import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Phone,
  Clock,
  TrendingUp,
  AlertTriangle,
  Users,
  Target,
  Calendar,
  BarChart3,
  RefreshCw,
  ArrowRight,
  Zap,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'sonner';

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#6366f1', '#14b8a6', '#f97316'];

const AnalyticsDashboard = () => {
  const { api } = useAuth();
  const [datePreset, setDatePreset] = useState('last_7_days');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [leadAging, setLeadAging] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [callSummary, setCallSummary] = useState(null);

  const fetchAnalytics = useCallback(async (showRefreshIndicator = false) => {
    try {
      if (showRefreshIndicator) setRefreshing(true);
      
      const [agingRes, perfRes, callsRes] = await Promise.all([
        api.get(`/analytics/lead-aging?date_preset=${datePreset}`),
        api.get(`/analytics/performance?date_preset=${datePreset}`),
        api.get(`/analytics/call-summary?date_preset=${datePreset}`)
      ]);
      
      setLeadAging(agingRes.data);
      setPerformance(perfRes.data);
      setCallSummary(callsRes.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [api, datePreset]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const formatHours = (hours) => {
    if (hours < 1) return `${Math.round(hours * 60)}m`;
    if (hours < 24) return `${Math.round(hours)}h`;
    return `${Math.round(hours / 24)}d`;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-64" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  // Prepare chart data
  const outcomeData = callSummary?.outcomeBreakdown 
    ? Object.entries(callSummary.outcomeBreakdown).map(([name, data]) => ({
        name: name.length > 12 ? name.substring(0, 12) + '...' : name,
        value: data.count
      }))
    : [];

  const hourlyData = performance?.hourDistribution || [];

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      {/* Header with Filter */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Performance Analytics</h2>
          <p className="text-muted-foreground">Track your team's sales performance</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={datePreset} onValueChange={setDatePreset}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="yesterday">Yesterday</SelectItem>
              <SelectItem value="last_7_days">Last 7 Days</SelectItem>
              <SelectItem value="this_month">This Month</SelectItem>
              <SelectItem value="last_month">Last Month</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            size="icon" 
            onClick={() => fetchAnalytics(true)}
            disabled={refreshing}
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Calls */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Calls</CardTitle>
            <Phone className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{callSummary?.totalCalls || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Avg {formatDuration(callSummary?.avgDuration)} per call
            </p>
          </CardContent>
        </Card>

        {/* Positive Outcomes */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Positive Outcomes</CardTitle>
            <Target className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">{callSummary?.positiveOutcomes || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {callSummary?.totalCalls > 0 
                ? `${Math.round((callSummary.positiveOutcomes / callSummary.totalCalls) * 100)}% success rate`
                : 'No calls yet'
              }
            </p>
          </CardContent>
        </Card>

        {/* Best Time to Call */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Best Time</CardTitle>
            <Clock className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              {performance?.bestClosingHour !== undefined 
                ? `${performance.bestClosingHour}:00`
                : 'N/A'
              }
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {performance?.bestClosingDay || 'Monday'}
            </p>
          </CardContent>
        </Card>

        {/* Stuck Leads Alert */}
        <Card className={leadAging?.stuckLeadsCount > 0 ? 'border-orange-300 bg-orange-50/50' : ''}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Stuck Leads</CardTitle>
            <AlertTriangle className={`h-4 w-4 ${leadAging?.stuckLeadsCount > 0 ? 'text-orange-500' : 'text-muted-foreground'}`} />
          </CardHeader>
          <CardContent>
            <div className={`text-3xl font-bold ${leadAging?.stuckLeadsCount > 0 ? 'text-orange-600' : ''}`}>
              {leadAging?.stuckLeadsCount || 0}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Leads inactive &gt; 48 hours
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Lead Aging Metrics */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Lead Conversion Timeline
          </CardTitle>
          <CardDescription>Average time spent in each stage</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-muted/30 rounded-lg">
              <p className="text-sm text-muted-foreground">New → Qualified</p>
              <p className="text-2xl font-bold text-blue-600">
                {formatHours(leadAging?.avgNewToQualified || 0)}
              </p>
              <p className="text-xs text-muted-foreground">
                {leadAging?.sampleSizes?.newToQualified || 0} leads
              </p>
            </div>
            <div className="flex items-center justify-center">
              <ArrowRight className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="text-center p-4 bg-muted/30 rounded-lg">
              <p className="text-sm text-muted-foreground">Qualified → Virtual Meet</p>
              <p className="text-2xl font-bold text-purple-600">
                {formatHours(leadAging?.avgQualifiedToMeet || 0)}
              </p>
              <p className="text-xs text-muted-foreground">
                {leadAging?.sampleSizes?.qualifiedToMeet || 0} leads
              </p>
            </div>
            <div className="flex items-center justify-center">
              <ArrowRight className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="text-center p-4 bg-muted/30 rounded-lg">
              <p className="text-sm text-muted-foreground">Meet → Conversion</p>
              <p className="text-2xl font-bold text-green-600">
                {formatHours(leadAging?.avgMeetToConversion || 0)}
              </p>
              <p className="text-xs text-muted-foreground">
                {leadAging?.sampleSizes?.meetToConversion || 0} leads
              </p>
            </div>
            <div className="flex items-center justify-center">
              <Zap className="h-5 w-5 text-yellow-500" />
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-muted-foreground">Total Conversion Time</p>
              <p className="text-2xl font-bold text-green-700">
                {formatHours(leadAging?.avgTotalConversionTime || 0)}
              </p>
              <p className="text-xs text-muted-foreground">
                {leadAging?.sampleSizes?.totalConversions || 0} conversions
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Call Outcomes */}
        <Card>
          <CardHeader>
            <CardTitle>Call Outcomes</CardTitle>
            <CardDescription>Distribution of call results</CardDescription>
          </CardHeader>
          <CardContent>
            {outcomeData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={outcomeData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {outcomeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                No call data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Hourly Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Call Activity by Hour</CardTitle>
            <CardDescription>When are calls being made?</CardDescription>
          </CardHeader>
          <CardContent>
            {hourlyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={hourlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" tickFormatter={(h) => `${h}:00`} />
                  <YAxis />
                  <Tooltip labelFormatter={(h) => `${h}:00 - ${h + 1}:00`} />
                  <Bar dataKey="calls" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                No call data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Executive Performance */}
      {performance?.executivePerformance?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Executive Performance
            </CardTitle>
            <CardDescription>Conversion rates and call metrics by team member</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium">Executive</th>
                    <th className="text-center py-3 px-4 font-medium">Leads</th>
                    <th className="text-center py-3 px-4 font-medium">Converted</th>
                    <th className="text-center py-3 px-4 font-medium">Rate</th>
                    <th className="text-center py-3 px-4 font-medium">Total Calls</th>
                    <th className="text-center py-3 px-4 font-medium">Calls/Lead</th>
                  </tr>
                </thead>
                <tbody>
                  {performance.executivePerformance.map((exec, index) => (
                    <tr key={exec.executiveId || index} className="border-b last:border-0 hover:bg-muted/50">
                      <td className="py-3 px-4 font-medium">{exec.executiveName}</td>
                      <td className="py-3 px-4 text-center">{exec.totalLeads}</td>
                      <td className="py-3 px-4 text-center text-green-600 font-medium">{exec.converted}</td>
                      <td className="py-3 px-4 text-center">
                        <Badge variant={exec.conversionRate >= 20 ? 'success' : exec.conversionRate >= 10 ? 'warning' : 'secondary'}>
                          {exec.conversionRate}%
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-center">{exec.totalCalls}</td>
                      <td className="py-3 px-4 text-center">{exec.callsPerLead}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stuck Leads Alert List */}
      {leadAging?.stuckLeads?.length > 0 && (
        <Card className="border-orange-300">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-orange-600">
              <AlertTriangle className="h-5 w-5" />
              Stuck Leads (Need Attention)
            </CardTitle>
            <CardDescription>Leads inactive for more than 48 hours</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {leadAging.stuckLeads.slice(0, 10).map((lead) => (
                <div key={lead.id} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                  <div>
                    <p className="font-medium">{lead.name}</p>
                    <p className="text-sm text-muted-foreground">
                      Status: {lead.status} • Assigned to: {lead.assignedTo || 'Unassigned'}
                    </p>
                  </div>
                  <Badge variant="destructive">
                    {Math.round(lead.hoursStuck)}h stuck
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
